import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../App";

const ATTENDEE_STYLES = {
  Delegate: "bg-[#4a90e2] text-white",
  Faculty: "bg-[#ef4036] text-white",
  Complimentary: "bg-[#10b981] text-white",
  Sponsor: "bg-[#ffa500] text-black",
  Trade: "bg-[#94a3b8] text-white",
};

const CONF_TAG_STYLES = {
  LIRSS: "bg-sky-500/20 text-sky-400 border border-sky-500/30",
  LIBSS: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
  Both: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
};

const LIRSS_LOGO = "https://lirss.org/wp-content/uploads/2026/03/LirssLoog.png";
const LIBSS_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_multi-conf-deploy/artifacts/060p67b5_Libss2026Logo-1%20%281%29.png";
const BG_IMAGE = "https://images.unsplash.com/photo-1549483249-f0b359d1e289?crop=entropy&cs=srgb&fm=jpg&q=85";

const HUB_TABS = [
  { key: "badge", label: "Badge & QR" },
  { key: "programme", label: "Programme" },
  { key: "faculty", label: "Faculty" },
];

const DelegateHub = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("badge");
  const [activeDay, setActiveDay] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hub/${token}`)
      .then(res => {
        setData(res.data);
        if (res.data.programme?.length) setActiveDay(res.data.programme[0].day);
      })
      .catch(err => setError(err.response?.data?.detail || "This Delegate Hub link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const shortDay = (day) => {
    const match = day.match(/^(\w+)\s+(\d+),/);
    return match ? `${match[1].slice(0, 3)} ${match[2]}` : day;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#070e1e] to-[#112447] flex items-center justify-center">
        <p className="text-[#a8c5f0]" data-testid="hub-loading">Loading your Delegate Hub...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#070e1e] to-[#112447] flex items-center justify-center p-4">
        <div className="bg-[#0f224a]/80 border border-[#ef4036] rounded-2xl p-8 max-w-md text-center" data-testid="hub-error">
          <p className="text-white font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  const { badge, qrValue, programme, faculty } = data;
  const badgeColor = ATTENDEE_STYLES[badge.attendeeType] || ATTENDEE_STYLES.Delegate;
  const regType = badge.registrationType || "";
  const isLirss = regType === "LIRSS Only" || regType === "LIRSS + LIBSS Combo";
  const isLibss = regType === "LIBSS Only" || regType === "LIRSS + LIBSS Combo";

  const groupedByDay = (programme || []).reduce((acc, session) => {
    (acc[session.day] = acc[session.day] || []).push(session);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#070e1e] to-[#112447]">

      {/* Branded header strip */}
      <header className="relative overflow-hidden border-b border-[#1e3a66]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${BG_IMAGE}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003f5c]/95 via-[#1d4382]/90 to-[#003f5c]/95" />
        <div className="relative max-w-2xl mx-auto px-4 py-5 flex items-center justify-between gap-4">
          {/* Left logo(s) */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {isLirss && (
              <div className="flex flex-col items-center">
                <div className="bg-white/95 rounded-lg p-1.5 shadow-lg border border-white/20">
                  <img src={LIRSS_LOGO} alt="LIRSS" className="h-8 sm:h-10 w-auto" />
                </div>
                <p className="text-[#a8c5f0] text-[10px] mt-1 font-medium whitespace-nowrap">Nov 25, 2026</p>
              </div>
            )}
            {isLibss && (
              <div className="flex flex-col items-center">
                <div className="bg-white/95 rounded-lg p-1.5 shadow-lg border border-white/20">
                  <img src={LIBSS_LOGO} alt="LIBSS 2026" className="h-8 sm:h-10 w-auto" />
                </div>
                <p className="text-[#a8c5f0] text-[10px] mt-1 font-medium whitespace-nowrap">Nov 26–27, 2026</p>
              </div>
            )}
          </div>

          {/* Centre text */}
          <div className="flex-1 text-center">
            <p className="text-white font-bold text-sm sm:text-base tracking-wide leading-tight">
              {regType === "LIRSS + LIBSS Combo"
                ? "LIBSS · LIRSS 2026"
                : regType === "LIBSS Only"
                ? "LIBSS 2026"
                : "LIRSS 2026"}
            </p>
            <p className="text-[#a8c5f0] text-xs mt-0.5">Homerton University Hospital, London</p>
          </div>

          {/* Right: Delegate Hub label */}
          <div className="flex-shrink-0 hidden sm:block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#ffa500] border border-[#ffa500]/40 px-2.5 py-1 rounded-full">
              Delegate Hub
            </span>
          </div>
        </div>
      </header>

      <div className="py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">

        {/* Top-level Hub tabs */}
        <div
          className="flex gap-1 border-b border-[#1e3a66] overflow-x-auto"
          data-testid="hub-top-tabs"
        >
          {HUB_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                activeTab === tab.key
                  ? "border-[#ef4036] text-white"
                  : "border-transparent text-[#94a3b8] hover:text-[#c3d3ea]"
              }`}
              data-testid={`hub-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Badge & QR ── */}
        {activeTab === "badge" && (
          <div
            className="bg-[#0f224a]/80 backdrop-blur-md rounded-2xl border border-[#1e3a66] p-6 sm:p-8 shadow-xl"
            data-testid="delegate-badge"
          >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <h1 className="font-heading text-2xl font-extrabold text-white">
                  {badge.title} {badge.firstName} {badge.lastName}
                </h1>
                <p className="text-[#a8c5f0] text-sm mt-1">{badge.country}</p>
              </div>
              <span
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${badgeColor}`}
                data-testid="attendee-type-badge"
              >
                {badge.attendeeType}
              </span>
            </div>
            {badge.delegateType && (
              <p className="text-[#c3d3ea] text-sm mb-2" data-testid="delegate-category">
                Delegate Category: <span className="font-semibold text-white">{badge.delegateType}</span>
              </p>
            )}
            <p className="text-[#ffa500] font-semibold text-sm" data-testid="registration-scope">
              {badge.displayLabel}
            </p>

            <div
              className="mt-6 flex flex-col items-center bg-white rounded-xl p-4 w-fit mx-auto"
              data-testid="qr-code-container"
            >
              <QRCodeSVG value={qrValue} size={180} />
              <p className="text-[#1d4382] text-xs font-medium mt-2">Scan for check-in</p>
            </div>
          </div>
        )}

        {/* ── Programme ── */}
        {activeTab === "programme" && (
          <div
            className="bg-[#0f224a]/80 backdrop-blur-md rounded-2xl border border-[#1e3a66] p-6 sm:p-8 shadow-xl"
            data-testid="hub-programme"
          >
            <h2 className="font-heading text-xl font-bold text-white mb-4">Your Programme</h2>

            {Object.keys(groupedByDay).length > 1 && (
              <div
                className="flex gap-1 mb-4 border-b border-[#1e3a66] overflow-x-auto"
                data-testid="programme-day-tabs"
              >
                {Object.keys(groupedByDay).map((day) => (
                  <button
                    key={day}
                    onClick={() => setActiveDay(day)}
                    className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                      activeDay === day
                        ? "border-[#ef4036] text-white"
                        : "border-transparent text-[#94a3b8] hover:text-[#c3d3ea]"
                    }`}
                    data-testid={`programme-tab-${shortDay(day).replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {shortDay(day)}
                  </button>
                ))}
              </div>
            )}

            <div
              data-testid={`programme-day-${(activeDay || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              {Object.keys(groupedByDay).includes(activeDay) && (
                <>
                  {Object.keys(groupedByDay).length === 1 && (
                    <h3 className="text-[#4a90e2] font-semibold mb-2">{activeDay}</h3>
                  )}
                  <div className="space-y-2">
                    {(groupedByDay[activeDay] || []).map((s) => (
                      <div
                        key={s._id}
                        className="border border-[#1e3a66] rounded-lg p-3 bg-[#0b1736]/60"
                      >
                        <div className="flex justify-between gap-3">
                          <span className="text-[#94a3b8] text-xs whitespace-nowrap">
                            {s.start}–{s.end}
                          </span>
                          <span className="text-white text-sm font-medium text-right">
                            {s.title === "TBC"
                              ? <span className="text-[#ffa500] italic">TBC (Abstract slot)</span>
                              : s.title}
                          </span>
                        </div>
                        {s.speaker && (
                          <p className="text-[#c3d3ea] text-xs mt-1 text-right">{s.speaker}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {!Object.keys(groupedByDay).length && (
                <p className="text-[#94a3b8] text-sm">No programme sessions available yet.</p>
              )}
            </div>
          </div>
        )}

        {/* ── Faculty ── */}
        {activeTab === "faculty" && (
          <div
            className="bg-[#0f224a]/80 backdrop-blur-md rounded-2xl border border-[#1e3a66] p-6 sm:p-8 shadow-xl"
            data-testid="hub-faculty"
          >
            <h2 className="font-heading text-xl font-bold text-white mb-1">Faculty</h2>
            <p className="text-[#94a3b8] text-xs mb-5">
              {faculty?.length ?? 0} speaker{faculty?.length !== 1 ? "s" : ""} at your conference
            </p>

            {!faculty?.length ? (
              <p className="text-[#94a3b8] text-sm">Faculty directory coming soon.</p>
            ) : (
              <div className="space-y-4" data-testid="faculty-list">
                {(faculty || []).map((member) => (
                  <div
                    key={member._id}
                    className="border border-[#1e3a66] rounded-xl p-4 bg-[#0b1736]/60"
                    data-testid={`faculty-member-${member._id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <div>
                        <p className="text-white font-semibold text-base leading-tight">
                          {member.name}
                        </p>
                        <p className="text-[#a8c5f0] text-xs mt-0.5">{member.country}</p>
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end" data-testid={`faculty-conf-tag-${member._id}`}>
                        {(member.conference === "Both" ? ["LIRSS", "LIBSS"] : [member.conference]).map(c => (
                          <span key={c} className={`text-xs font-bold px-2.5 py-1 rounded-full ${CONF_TAG_STYLES[c]}`}>
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-[#c3d3ea] text-sm leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
      </div>
    </div>
  );
};

export default DelegateHub;
